import { Info } from "lucide-react";
import { Skeleton } from "../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { cn } from "../../../../lib/utils";
import { ProductRowViewModel } from "../types/productListView.types";
import { PRODUCT_TABLE_COLUMNS, productTableColumnClass } from "./productTable.columns";
import ProductTableRow from "./ProductTableRow";

const COLUMN_COUNT = PRODUCT_TABLE_COLUMNS.length + 1; // + actions column
const ROW_SKELETON_COUNT = 5;

interface ProductTableProps {
  rows: ProductRowViewModel[];
  isLoading: boolean;
  onView: (row: ProductRowViewModel) => void;
  onEdit: (row: ProductRowViewModel) => void;
  onDelete: (row: ProductRowViewModel) => void;
}

/**
 * Table shell styled after `IngredientTable`
 * (src/screens/lcaIngredients/ingredientList/components/IngredientTable.tsx)
 * — same card container, fixed-width header cells tinted with the primary
 * color, and per-column skeleton rows while loading. Replaces the legacy
 * Semantic UI `CommonTable` (src/components/commonTable/CommonTable.tsx),
 * including its per-column info tooltip (see `ProductTableCol` in
 * src/config/tableColConstant.ts) rebuilt on the common `Tooltip`.
 */
const ProductTable = ({ rows, isLoading, onView, onEdit, onDelete }: ProductTableProps) => {
  return (
    <div className="tw-relative tw-min-h-[280px] tw-overflow-x-auto tw-rounded-lg tw-border tw-border-border tw-bg-white">
      <TooltipProvider delayDuration={200}>
        <Table className="tw-w-full tw-table-fixed [&_td]:tw-px-4 [&_td]:tw-py-3 [&_th]:tw-px-4 [&_th]:tw-py-3">
          <TableHeader>
            <TableRow className="tw-border-b-0 tw-bg-primary/5 hover:tw-bg-primary/5 [&_th]:tw-bg-primary/5 [&_th]:tw-text-primary [&_th]:tw-font-semibold [&_th]:tw-align-middle">
              {PRODUCT_TABLE_COLUMNS.map(column => (
                <TableHead
                  key={column.key}
                  className={cn(productTableColumnClass[column.key], "tw-text-primary")}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-primary">
                    {column.label}
                    {column.information ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-primary/70" />
                        </TooltipTrigger>
                        <TooltipContent>{column.information}</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </span>
                </TableHead>
              ))}
              <TableHead className={cn(productTableColumnClass.actions, "tw-text-right tw-text-primary")}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(ROW_SKELETON_COUNT)].map((_, index) => (
                <TableRow key={index} className="tw-bg-white">
                  {[...Array(COLUMN_COUNT)].map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="tw-h-4 tw-w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow className="tw-bg-white hover:tw-bg-white">
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="tw-py-12 tw-text-center tw-text-muted-foreground"
                >
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <ProductTableRow
                  key={row.product_id}
                  row={row}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
};

export default ProductTable;
